/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-04 15:54:00
 */

import React from 'react'
import dayjs from 'dayjs'
import CONFIG from '../../../constants/config'
import PopoverCard from '../popover'
import baseColumnsData, { customerColumns } from './baseColumnsData'

// 类型定义
export interface PhotoWatermarkItem {
  seq: string
  customer?: {
    exist: boolean
    customerOwners?: Array<{
      userDepartments: Array<{ id: string; name: string }>
      userMobile: string
      userName: string
    }>
    customerLabelGroups?: Array<{
      labelGroupID: string
      labelGroupName: string
      labels: Array<{ labelName: string }>
      rank: number
    }>
    customID?: string
  }
  neteaseShelfReview?: {
    exist: boolean
    reviewResult: number
    reviewType: number
    operator?: { nickname: string }
  }
  aiRecognitionResult?: Array<{ key: number; value: string }>
  extends?: Array<{ title: string; content: string }>
  mediaType?: number
  smallURL?: string
  isLimitViewByPhotoCount?: boolean
}

export interface AiExtractConfig {
  isEnabled: boolean
  selectedScenarios: string[]
  extractedPhotos: Set<string>
  isExtracting: boolean
  isFilterEnabled?: boolean
}

export interface AiRecognitionResult {
  photoID: string
  aiRecognitionResult: Array<{ key: number; value: string }>
}

interface ColumnConfig {
  title: string
  dataIndex: string | string[]
  key?: string
  width: number | string | undefined
  rank?: number
  render?: (text: any, record: any) => React.ReactNode
  onCell?: (record: any) => { style?: React.CSSProperties } | undefined
}

/**
 * antd6 不再把点号字符串 dataIndex 拆成嵌套路径（'a.b' 会按单键 record['a.b'] 取值），
 * 嵌套取值必须用数组路径。列身份（key、列设置勾选、localStorage、导出匹配）仍沿用
 * 原点号字符串，仅在生成表格列时转换。
 */
const toDataIndexPath = (dataIndex: string): string | string[] =>
  dataIndex.includes('.') ? dataIndex.split('.') : dataIndex

interface CustomerConfig {
  noun?: string
  [key: string]: any
}

type PlainFilter = {
  keyword?: string
  mediaType?: number
  times?: any[]
  departments?: any[]
  departmentUsers?: any[]
  watermarks?: any[]
  existPhotoComment?: string
}

const departmentTitles = [
  '一级部门',
  '二级部门',
  '三级部门',
  '四级部门',
  '五级部门',
  '六级部门',
  '七级部门',
  '八级部门',
  '九级部门',
  '十级部门',
  '十一级部门',
  '十二级部门',
  '十三级部门',
  '十四级部门',
  '十五级部门',
  '十六级部门',
  '十七级部门',
  '十八级部门',
  '十九级部门',
  '二十级部门'
]

export const createDepartmentsColumnsObject = (
  dataSource: PhotoWatermarkItem[]
): ColumnConfig[] => {
  if (!dataSource || dataSource.length === 0) return []
  const departmentsLevel = Math.max(
    ...dataSource.map((item) => {
      if (item?.customer?.exist) {
        const owners = item.customer.customerOwners || []
        return Math.max(...owners.map((o) => o.userDepartments.length), 0)
      }
      return 0
    })
  )

  const columns: ColumnConfig[] = []

  for (let i = 0; i < departmentsLevel; i++) {
    columns.push({
      title: departmentTitles[i],
      dataIndex: `department${i}`,
      key: `department${i}`,
      width: 120,
      render: (_, record) => {
        if (!record?.customer?.exist) return null
        const curLevelDepartments = record.customer.customerOwners
          .map((o) => (o.userDepartments ? o.userDepartments[i] : null))
          .reduce((acc, cur) => {
            if (cur && acc.findIndex((a) => a.id === cur.id) === -1) {
              acc.push(cur)
            }
            return acc
          }, [])
        if (curLevelDepartments.length === 0) return null
        if (curLevelDepartments.length === 1)
          return <div>{curLevelDepartments[0].name}</div>
        return (
          <>
            {curLevelDepartments.map((d, index) => (
              <div key={index}>
                {d.name}
                {index < curLevelDepartments.length - 1 ? '、' : ''}
              </div>
            ))}
          </>
        )
      }
    })
  }
  return columns
}

export const createBaseColumnsObject = (
  checkedBaseColumns: string[]
): ColumnConfig[] => {
  return baseColumnsData
    .filter((column) => {
      // 使用key或dataIndex进行匹配
      const columnKey = column.key || column.dataIndex
      return checkedBaseColumns.includes(columnKey)
    })
    .map((column) => {
      if (column.dataIndex === 'time') {
        return {
          key: column.key || column.dataIndex,
          title: '拍摄时间',
          width: column.width,
          dataIndex: 'time',
          render: (text) => dayjs.unix(text).format(CONFIG.timeFormat)
        }
      }

      // 处理审核结果列
      if (column.dataIndex === 'reviewResult') {
        return {
          key: column.key || column.dataIndex,
          title: column.title,
          width: column.width,
          dataIndex: column.dataIndex,
          render: (text, record) => {
            // 判断是否存在审核信息
            if (
              !record.neteaseShelfReview ||
              !record.neteaseShelfReview.exist
            ) {
              return <span>未设置ai审核规范</span>
            }

            // 根据reviewResult展示不同状态
            const { reviewResult } = record.neteaseShelfReview
            if (reviewResult === -1) {
              return <span>AI审核中</span>
            }
            if (reviewResult === 0) {
              return <span>不合格</span>
            }
            if (reviewResult === 1) {
              return <span>合格</span>
            }

            // 其他情况（包括reviewResult === 2）
            return <span>不计入</span>
          }
        }
      }

      // 处理审核人列
      if (column.dataIndex === 'reviewOperator') {
        return {
          key: column.key || column.dataIndex,
          title: column.title,
          width: column.width,
          dataIndex: column.dataIndex,
          render: (text, record) => {
            // 判断是否存在审核信息
            if (
              !record.neteaseShelfReview ||
              !record.neteaseShelfReview.exist
            ) {
              return <span>未设置ai审核规范</span>
            }

            // 如果是AI审核中
            const { reviewResult, reviewType, operator } =
              record.neteaseShelfReview
            if (reviewResult === -1) {
              return <span>AI审核中</span>
            }

            // 如果是AI审核
            if (reviewType === 1) {
              return <span>AI审核</span>
            }

            // 其他情况返回操作人昵称
            return <span>{operator?.nickname || '-'}</span>
          }
        }
      }

      return {
        key: column.key || column.dataIndex,
        title: column.title,
        width: column.width,
        dataIndex: toDataIndexPath(column.dataIndex),
        render: column.render
      }
    })
}

export const createExtraColumnsObject = (
  checkedExtraColumns: string[],
  extraColumnsTitles: string[]
): ColumnConfig[] => {
  return extraColumnsTitles
    .filter((column) => checkedExtraColumns.includes(column))
    .map((title) => {
      return {
        key: title,
        title,
        dataIndex: title,
        width: 140,
        render: (_: any, record: PhotoWatermarkItem) => {
          const extendItem = record?.extends?.find(
            (item) => item.title === title
          )
          return <PopoverCard contentItem={extendItem}></PopoverCard>
        }
      }
    })
}

export const createCustomerTagColumnsObject = (
  dataSource: PhotoWatermarkItem[]
): ColumnConfig[] => {
  const columns: ColumnConfig[] = []
  const set = new Set<string>()

  dataSource.forEach((data) => {
    if (!data.customer?.exist) return
    const customerLabelGroups = data.customer.customerLabelGroups || []
    customerLabelGroups.forEach(({ labelGroupID, labelGroupName, rank }) => {
      if (!set.has(labelGroupID)) {
        set.add(labelGroupID)

        const newColumn: ColumnConfig = {
          title: labelGroupName,
          dataIndex: labelGroupID,
          key: labelGroupID,
          width: 140,
          rank,
          render: (_: any, record: PhotoWatermarkItem) => {
            const labelGroup = record.customer?.customerLabelGroups?.find(
              (item) => item.labelGroupID === labelGroupID
            )

            if (labelGroup) {
              return labelGroup.labels.map((item) => item.labelName).join(',')
            }

            return ''
          }
        }

        const insertIndex = columns.findIndex((item) => {
          return (
            item.rank !== undefined && rank !== undefined && item.rank < rank
          )
        })

        if (insertIndex === -1) {
          columns.push(newColumn)
        } else {
          columns.splice(insertIndex, 0, newColumn)
        }
      }
    })
  })
  return columns
}

/**
 * 145 团队开启拜访模式，才有extension.visitExplicitInfo，才展示客户管理相关的列
 *
 * 160 使用customer字段集合拜访模式相关数据，要customer.exist === true, 才是开启拜访拍照的照片
 */

export const createCustomerColumnsObject = (
  dataSource: PhotoWatermarkItem[],
  customerConfig: CustomerConfig
): ColumnConfig[] => {
  const hasCustomerColumns = dataSource.some((item) => item?.customer?.exist)
  if (hasCustomerColumns) {
    const customerTagColumns = createCustomerTagColumnsObject(dataSource)
    const baseColumns: ColumnConfig[] = customerColumns.map((item) => ({
      key: item.dataIndex,
      title: item.title,
      width: item.width,
      dataIndex: toDataIndexPath(item.dataIndex)
    }))
    return baseColumns.concat(customerTagColumns).concat([
      {
        title: `${customerConfig.noun || '客户'}ID`,
        dataIndex: toDataIndexPath('customer.customID'),
        key: 'customer.customID',
        width: 120
      },
      {
        title: '跟进人及电话',
        dataIndex: toDataIndexPath('customer.customerOwners'),
        key: 'customer.customerOwners',
        width: 200,
        render: (_: any, record: PhotoWatermarkItem) => {
          if (!record.customer?.exist) return null
          return (
            <>
              {(record.customer.customerOwners || []).map(
                ({ userMobile, userName }, index) => (
                  <div key={index}>{`${userName} - ${userMobile}`}</div>
                )
              )}
            </>
          )
        }
      }
    ])
  }
  return []
}

// export const createCommentColumnObject = (dataSource) => {
//   const hasCommentColumn = dataSource.some((item) => item.extension.comments)
//   if (hasCommentColumn) {
//     return {
//       title: '评论444',
//       width: 300,
//       key: 'comments',
//       dataIndex: 'extension.comments'
//     }
//   }
//   return []
// }

// 2025-02-13 0213 迭代将 得分和打分人改为 base 字段
// const createScoreColumns = () => [
//   {
//     title: '得分',
//     key: 'score',
//     dataIndex: 'score',
//     width: 115,
//     render: (text) => {
//       return <div>{String(text) ? text : '-'}</div>
//     }
//   },
//   {
//     title: '打分人',
//     key: 'scorerName',
//     dataIndex: 'scorerName',
//     width: 115,
//     render: (text) => {
//       return <div>{text ? text : '-'}</div>
//     }
//   }
// ]

/**
 * 修改（大团队）团队查看照片受限时，插入的卡点行（最后一页的最后一行）的渲染逻辑和单元格属性
 *  - onCell: 卡点行，padding: 0
 *  - render:
 *    - 卡点行
 *      - 1st Column，合并整行（colSpan = columns.length），并渲染limitRowContent
 *      - 其余列不显示（colSpan = 0）， 渲染null
 *    - 非卡点行，正常渲染
 * @param {*} columns 除fixed照片列外的所有列
 * @param {*} limitRowContent 卡点行内容
 * @returns 修改后的columns
 */
const modifyGroupPhotoViewLimitRowRender = (
  columns: ColumnConfig[],
  limitRowContent: React.ReactNode
): ColumnConfig[] => {
  return columns.map((column, index) => {
    return {
      ...column,
      onCell: (record: PhotoWatermarkItem) => {
        if (record.seq === 'groupPhotoViewLimit') {
          return {
            style: {
              padding: 0
            }
          }
        }
        return undefined
      },
      render: (value: any, record: PhotoWatermarkItem) => {
        if (record.seq === 'groupPhotoViewLimit') {
          if (index === 0) {
            return {
              children: limitRowContent,
              props: {
                colSpan: columns.length
              }
            }
          }
          return {
            children: null,
            props: {
              colSpan: 0
            }
          }
        } else {
          // 如果有自定义render函数，则返回自定义render函数
          // 如果没有自定义render函数（render === undefined），则返回value
          return column.render ? column.render(value, record) : value
        }
      }
    }
  })
}

// AI提取相关列配置
export const createAiColumnsObject = (
  aiExtractConfig: AiExtractConfig | null,
  checkedAiColumns: string[] = [],
  aiRecognitionResults: AiRecognitionResult[] = []
): ColumnConfig[] => {
  if (
    !aiExtractConfig ||
    !aiExtractConfig.isEnabled ||
    !aiExtractConfig.selectedScenarios.length ||
    !checkedAiColumns.length
  ) {
    return []
  }

  const aiColumns: ColumnConfig[] = []

  // 创建AI结果映射，方便查找（来自独立AI识别接口）
  const aiResultsMap = new Map<string, Array<{ key: number; value: string }>>()
  aiRecognitionResults.forEach((result) => {
    if (result.photoID && result.aiRecognitionResult) {
      aiResultsMap.set(result.photoID, result.aiRecognitionResult)
    }
  })

  // 根据选中的场景和已勾选的AI列添加对应的列
  aiExtractConfig.selectedScenarios.forEach((scenarioId) => {
    const scenario = AI_EXTRACT_SCENARIOS[scenarioId]
    if (scenario && scenario.columns) {
      scenario.columns.forEach((column) => {
        // 只添加在列显示设置中被勾选的AI列
        if (checkedAiColumns.includes(column.dataIndex)) {
          aiColumns.push({
            key: column.dataIndex,
            title: column.title,
            width: column.width || 150,
            dataIndex: column.dataIndex,
            render: (_text: any, record: PhotoWatermarkItem) => {
              // 首先尝试从独立AI识别接口的结果中查找
              const photoResults = aiResultsMap.get(record.seq)
              if (photoResults && Array.isArray(photoResults)) {
                const matchingResult = photoResults.find(
                  (item) => item.key === scenario.enumValue && item.value
                )
                if (matchingResult) {
                  return matchingResult.value
                }
              }

              // 如果独立接口没有结果，尝试从照片数据本身的aiRecognitionResult中查找
              if (
                record.aiRecognitionResult &&
                Array.isArray(record.aiRecognitionResult)
              ) {
                const matchingResult = record.aiRecognitionResult.find(
                  (item) => item.key === scenario.enumValue && item.value
                )
                if (matchingResult) {
                  return matchingResult.value
                }
              }

              return ''
            }
          })
        }
      })
    }
  })

  return aiColumns
}

interface CreateColumnsOptions {
  extraColumnsTitles: string[]
  checkedBaseColumns: string[]
  checkedExtraColumns: string[]
  customerConfig: CustomerConfig
  limitRowContent?: React.ReactNode
  aiExtractConfig: AiExtractConfig | null
  checkedAiColumns: string[]
  aiRecognitionResults?: AiRecognitionResult[]
}

export const createColumns = (
  dataSource: PhotoWatermarkItem[],
  {
    extraColumnsTitles,
    checkedBaseColumns,
    checkedExtraColumns,
    customerConfig,
    limitRowContent = null,
    aiExtractConfig,
    checkedAiColumns,
    aiRecognitionResults = []
  }: CreateColumnsOptions
): ColumnConfig[] => {
  const baseColumns = createBaseColumnsObject(checkedBaseColumns)
  const extraColumns = createExtraColumnsObject(
    checkedExtraColumns,
    extraColumnsTitles
  )
  const customerColumns = createCustomerColumnsObject(
    dataSource,
    customerConfig
  )
  const departmentsColumns = createDepartmentsColumnsObject(dataSource)
  const aiColumns = createAiColumnsObject(
    aiExtractConfig,
    checkedAiColumns,
    aiRecognitionResults
  )
  // const commentColumn = createCommentColumnObject(dataSource)

  // 重新组织列的顺序：AI列 + 基础列 + 其他列
  const result = aiColumns.concat(
    // AI列在最前面
    baseColumns,
    extraColumns,
    customerColumns,
    departmentsColumns
  )
  // .concat(commentColumn)

  return modifyGroupPhotoViewLimitRowRender(result, limitRowContent)
}

export const calcPickedMemberNum = (
  filter: PlainFilter,
  pickerUserCount: number
): number => {
  const departments = filter.departments || []
  const departmentUsers = filter.departmentUsers || []

  const userNumInDepartments = departments
    .map((department: any) => department.num || 0)
    .reduce((acc: number, cur: number) => acc + cur, 0)
  const total = departmentUsers.length + userNumInDepartments
  const hasSelection = departmentUsers.length > 0 || departments.length > 0
  if (
    (!hasSelection && total === 0) ||
    total === pickerUserCount ||
    departments.some((department: any) => department.id === '0')
  ) {
    return pickerUserCount
  } else {
    return total
  }
}

interface FetchPhotosFilter {
  keywords: string[]
  mediaType: number
  times: Array<{ start: string; end: string }>
  userIDList: any
  departmentIDList: any
  watermarks: Array<{ watermarkBaseID: string; groupWatermarkID: string }>
  existPhotoComment: number
  photoTags?: number[]
}

// 普通对象格式的 filter 类型
interface PlainPhotoWatermarkFilter {
  keyword?: string
  mediaType?: number
  times?: any[]
  departments?: Array<{ id: string; name?: string; num?: number }>
  departmentUsers?: Array<{ userID: string; [key: string]: any }>
  watermarks?: string[]
  existPhotoComment?: string
}

export const buildFetchPhotosParams = (
  filter: PlainPhotoWatermarkFilter | null,
  groupID: string,
  lastPageCond = '',
  onlyInGroup: number | AiExtractConfig | null = 0,
  aiExtractConfig: AiExtractConfig | null = null
) => {
  // 对齐 origin/master：aiExtractConfig 只取第 5 个入参。
  // 仅 handleFetchPhotos 用 5 参形式（在第 5 位传 config）才会应用 photoTags 筛选；
  // handleFetchPhotosWithConfig / handleFetchExtraColumns / handleToggleAiFilter 用 4 参形式（config 落在 onlyInGroup 位）时，
  // 基准侧 aiExtractConfig 为 null、不加 photoTags 筛选——首次提取需先按全量照片拉取再识别，否则筛到「已标记照片」为空导致「没有进行提取」。
  // 第 4 位若为对象（旧式调用），等价于基准侧落在 onlyInGroup 槽：仅参与 includeQuitGroupUser 判定（对象 !== ONLY_IN_GROUP 恒为 true），不当作 config。
  const actualOnlyInGroup =
    typeof onlyInGroup === 'object' && onlyInGroup !== null
      ? 0
      : (onlyInGroup as number)
  const actualAiExtractConfig = aiExtractConfig

  if (!filter) {
    return {
      filter: {
        keywords: [],
        mediaType: -1,
        times: [],
        userIDList: [],
        departmentIDList: [],
        watermarks: [],
        existPhotoComment: 0
      },
      groupID,
      lastPageCond,
      includeQuitGroupUser: true
    }
  }

  // 使用普通对象格式的 filter
  const departments = filter.departments || []
  const departmentUsers = filter.departmentUsers || []
  const watermarks = filter.watermarks || []
  const times = filter.times || []

  const departmentIDList = departments.map((department) => department.id)
  const userIDList = departmentUsers.map((user) => user.userID)

  // 构建基础筛选参数
  const baseFilter: FetchPhotosFilter = {
    keywords: filter.keyword ? [filter.keyword] : [],
    mediaType: filter.mediaType ?? -1,
    times:
      times.length >= 2
        ? [
            {
              start: times[0].format(CONFIG.timeFormat),
              end: times[1].format(CONFIG.timeFormat)
            }
          ]
        : [],
    userIDList: userIDList,
    departmentIDList: departmentIDList,
    watermarks: watermarks.map((watermark: string) => ({
      watermarkBaseID: watermark.split('+')[0],
      groupWatermarkID: watermark.split('+')[1]
    })),
    existPhotoComment: Number.parseInt(filter.existPhotoComment || '0'),
    labelIDs: filter.labelIDs || [],
    labelMatchAll: filter.labelMatchAll || false
  }

  // 如果启用了AI提取功能并且筛选开关打开，添加photoTags筛选
  if (
    actualAiExtractConfig &&
    actualAiExtractConfig.isEnabled &&
    actualAiExtractConfig.isFilterEnabled &&
    actualAiExtractConfig.selectedScenarios.length > 0
  ) {
    const photoTags = buildPhotoTagsFilter(
      actualAiExtractConfig.selectedScenarios
    )
    if (photoTags.length > 0) {
      baseFilter.photoTags = photoTags
    }
  }

  return {
    filter: baseFilter,
    groupID,
    lastPageCond,
    // 添加 includeQuitGroupUser 字段
    includeQuitGroupUser: actualOnlyInGroup !== CONFIG.onlyInGroup.ONLY_IN_GROUP
  }
}

// 根据选中的AI场景构建photoTags筛选条件
// AI提取场景配置映射
export const AI_EXTRACT_SCENARIOS = {
  car_dashboard_mileage: {
    key: 'car_dashboard_mileage',
    title: '汽车仪表盘-里程',
    label: '汽车仪表盘-里程',
    photoTags: [38, 40], // 根据映射表：汽车仪表盘-里程对应[38, 40]
    resultKey: 'mileage',
    enumValue: 1,
    columns: [
      {
        dataIndex: 'ai_mileage',
        title: '汽车仪表盘-里程',
        width: 150
      }
    ]
  },
  fuel_gauge_liters: {
    key: 'fuel_gauge_liters',
    title: '加油表-升数',
    label: '加油表-升数',
    photoTags: [38], // 根据映射表：加油表-升数对应[38]
    resultKey: 'fuel_volume',
    enumValue: 2,
    columns: [
      {
        dataIndex: 'ai_fuel_volume',
        title: '加油表-升数',
        width: 150
      }
    ]
  },
  fuel_gauge_unit_price: {
    key: 'fuel_gauge_unit_price',
    title: '加油表-单价',
    label: '加油表-单价',
    photoTags: [38], // 根据映射表：加油表-单价对应[38]
    resultKey: 'fuel_price',
    enumValue: 3,
    columns: [
      {
        dataIndex: 'ai_fuel_price',
        title: '加油表-单价',
        width: 150
      }
    ]
  },
  fuel_gauge_total_price: {
    key: 'fuel_gauge_total_price',
    title: '加油表-总价',
    label: '加油表-总价',
    photoTags: [38], // 根据映射表：加油表-总价对应[38]
    resultKey: 'fuel_total',
    enumValue: 4,
    columns: [
      {
        dataIndex: 'ai_fuel_total',
        title: '加油表-总价',
        width: 150
      }
    ]
  },
  water_meter_cubic: {
    key: 'water_meter_cubic',
    title: '水表-立方数',
    label: '水表-立方数',
    photoTags: [39], // 根据映射表：水表-立方数对应[39]
    resultKey: 'water_volume',
    enumValue: 5,
    columns: [
      {
        dataIndex: 'ai_water_volume',
        title: '水表-立方数',
        width: 150
      }
    ]
  },
  electricity_meter_degrees: {
    key: 'electricity_meter_degrees',
    title: '电表-度数',
    label: '电表-度数',
    photoTags: [39], // 根据映射表：电表-度数对应[39]
    resultKey: 'electric_degree',
    enumValue: 6,
    columns: [
      {
        dataIndex: 'ai_electric_degree',
        title: '电表-度数',
        width: 150
      }
    ]
  }
}

// 根据选中的AI场景构建photoTags筛选条件
export const buildPhotoTagsFilter = (selectedScenarios: string[]): number[] => {
  if (!selectedScenarios || selectedScenarios.length === 0) {
    return []
  }

  const photoTags = new Set<number>()
  selectedScenarios.forEach((scenarioKey) => {
    const scenario = AI_EXTRACT_SCENARIOS[scenarioKey]
    if (scenario && scenario.photoTags) {
      scenario.photoTags.forEach((tag) => photoTags.add(tag))
    }
  })

  return Array.from(photoTags)
}
